import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const ConveyancingMatters = () => {
    const [matters, setMatters] = useState([]);
    const [filteredMatters, setFilteredMatters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null); // eslint-disable-line no-unused-vars
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterType, setFilterType] = useState('');
    const [toast, setToast] = useState(null);
    const [clients, setClients] = useState([]);
    const [trustAccounts, setTrustAccounts] = useState([]);
    const [saving, setSaving] = useState(false);

    // ESC key to close modal (HCI: User Control & Freedom)
    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') setShowModal(false); };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);
    const navigate = useNavigate();

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    const [formData, setFormData] = useState({
        title: '',
        transfer_type: 'SALE',
        property_description: '',
        property_type: 'RESIDENTIAL',
        property_address: '',
        title_deed_number: '',
        erf_number: '',
        purchase_price: '0.00',
        transfer_duty: '0.00',
        bond_amount: '0.00',
        currency: 'USD',
        primary_client: '',
        trust_account: '',
        instruction_date: new Date().toISOString().split('T')[0],
        target_registration_date: '',
        notes: ''
    });

    useEffect(() => {
        fetchMatters();
        fetchClients();
        fetchTrustAccounts();
    }, []);

    useEffect(() => {
        let filtered = matters;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(m =>
                m.reference_number?.toLowerCase().includes(term) ||
                m.title?.toLowerCase().includes(term) ||
                m.property_address?.toLowerCase().includes(term) ||
                m.client_name?.toLowerCase().includes(term)
            );
        }
        if (filterStatus) {
            filtered = filtered.filter(m => m.status === filterStatus);
        }
        if (filterType) {
            filtered = filtered.filter(m => m.transfer_type === filterType);
        }
        setFilteredMatters(filtered);
    }, [matters, searchTerm, filterStatus, filterType]);

    const fetchMatters = async () => {
        setError(null);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/conveyancing/matters/', {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!response.ok) throw new Error('Failed to fetch matters');
            const data = await response.json();
            setMatters(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/clients/', {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setClients(Array.isArray(data) ? data : data.results || []);
            }
        } catch (err) { /* silently fail */ }
    };

    const fetchTrustAccounts = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/trust/accounts/?is_active=true', {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setTrustAccounts(Array.isArray(data) ? data : data.results || []);
            }
        } catch (err) { /* silently fail */ }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const token = localStorage.getItem('accessToken');
            const submitData = { ...formData };
            if (!submitData.trust_account) delete submitData.trust_account;
            if (!submitData.target_registration_date) delete submitData.target_registration_date;

            const response = await fetch('/api/conveyancing/matters/', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(submitData)
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(JSON.stringify(errorData));
            }
            const newMatter = await response.json();
            setShowModal(false);
            showToast('Conveyancing matter created successfully');
            navigate(`/conveyancing/matters/${newMatter.id}`);
        } catch (err) {
            showToast(err.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
    };

    const statusColors = {
        'INSTRUCTION': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        'SEARCHES': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        'DRAFTING': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
        'SIGNING': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
        'LODGEMENT': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
        'REGISTRATION': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
        'COMPLETED': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
        'CANCELLED': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        'ON_HOLD': 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    };

    const transferTypes = [
        { value: 'SALE', label: 'Sale and Transfer' },
        { value: 'BOND', label: 'Bond Registration' },
        { value: 'BOND_CANCEL', label: 'Bond Cancellation' },
        { value: 'SECTIONAL', label: 'Sectional Title Transfer' },
        { value: 'DONATION', label: 'Donation' },
        { value: 'ESTATE', label: 'Estate Transfer' },
        { value: 'SUBDIVISION', label: 'Subdivision Transfer' },
        { value: 'CONSOLIDATION', label: 'Consolidation Transfer' },
    ];

    const propertyTypes = [
        { value: 'RESIDENTIAL', label: 'Residential' },
        { value: 'COMMERCIAL', label: 'Commercial' },
        { value: 'AGRICULTURAL', label: 'Agricultural' },
        { value: 'INDUSTRIAL', label: 'Industrial' },
        { value: 'VACANT_LAND', label: 'Vacant Land' },
        { value: 'SECTIONAL', label: 'Sectional Title' },
    ];

    const currencies = [
        { value: 'USD', label: 'USD' }, { value: 'ZWL', label: 'ZWL' },
        { value: 'ZAR', label: 'ZAR' }, { value: 'GBP', label: 'GBP' }, { value: 'EUR', label: 'EUR' },
    ];

    const statuses = [
        { value: 'INSTRUCTION', label: 'Instruction' }, { value: 'SEARCHES', label: 'Searches' },
        { value: 'DRAFTING', label: 'Drafting' }, { value: 'SIGNING', label: 'Signing' },
        { value: 'LODGEMENT', label: 'Lodgement' }, { value: 'REGISTRATION', label: 'Registration' },
        { value: 'COMPLETED', label: 'Completed' }, { value: 'CANCELLED', label: 'Cancelled' },
        { value: 'ON_HOLD', label: 'On Hold' },
    ];

    if (loading) {
        return (
            <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <div className="h-4 w-40 bg-gray-200 rounded animate-pulse mb-3"></div>
                        <div className="h-7 w-52 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    <div className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div className="space-y-4">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                            <div className="h-5 w-40 bg-gray-200 rounded animate-pulse mb-2"></div>
                            <div className="h-3 w-64 bg-gray-100 rounded animate-pulse"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            {/* Toast notification */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg text-white font-medium transition-all ${toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
                    {toast.message}
                </div>
            )}

            {/* Header */}
            <div className="mb-6">
                <nav className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    <Link to="/conveyancing" className="hover:text-blue-600 dark:hover:text-blue-400">Conveyancing</Link>
                    <span className="mx-2">&rsaquo;</span>
                    <span>All Matters</span>
                </nav>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Conveyancing Matters</h1>
                    <button
                        onClick={() => setShowModal(true)}
                        className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm transition-colors"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        New Matter
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="Search by reference, title, address, client..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Statuses</option>
                        {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Types</option>
                        {transferTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </div>
            </div>

            {/* Matters List */}
            {filteredMatters.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                    <div className="mx-auto w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No Conveyancing Matters</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-6">Create your first conveyancing matter to get started.</p>
                    <button onClick={() => setShowModal(true)} className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Create Matter
                    </button>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredMatters.map(matter => (
                        <Link
                            key={matter.id}
                            to={`/conveyancing/matters/${matter.id}`}
                            className="block bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md transition-all"
                        >
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-mono text-blue-600 dark:text-blue-400">{matter.reference_number}</span>
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[matter.status] || 'bg-gray-100 text-gray-700'}`}>
                                                {matter.status_display}
                                            </span>
                                        </div>
                                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">{matter.title}</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{matter.property_address}</p>
                                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 dark:text-gray-500">
                                            <span>Client: {matter.client_name}</span>
                                            <span>{matter.transfer_type_display}</span>
                                            {matter.trust_account_name && <span>Trust: {matter.trust_account_name}</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                                        {formatCurrency(matter.purchase_price, matter.currency)}
                                    </p>
                                    {matter.completion_percentage !== undefined && (
                                        <div className="mt-2">
                                            <div className="flex items-center gap-2">
                                                <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full">
                                                    <div
                                                        className="h-1.5 bg-blue-500 rounded-full transition-all"
                                                        style={{ width: `${matter.completion_percentage}%` }}
                                                    ></div>
                                                </div>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">{matter.completion_percentage}%</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {/* Create Matter Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl mx-4 mb-8">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">New Conveyancing Matter</h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Matter Title *</label>
                                <input
                                    type="text" required
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="e.g. Transfer of Stand 123 Harare"
                                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Transfer Type *</label>
                                    <select value={formData.transfer_type} onChange={(e) => setFormData({ ...formData, transfer_type: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        {transferTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Property Type *</label>
                                    <select value={formData.property_type} onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        {propertyTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Property Address *</label>
                                <textarea required value={formData.property_address} onChange={(e) => setFormData({ ...formData, property_address: e.target.value })}
                                    rows={2} placeholder="Full property address"
                                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Property Description *</label>
                                <textarea required value={formData.property_description} onChange={(e) => setFormData({ ...formData, property_description: e.target.value })}
                                    rows={2} placeholder="e.g. Stand 1234, Harare Township, measuring 800m²"
                                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title Deed Number</label>
                                    <input type="text" value={formData.title_deed_number} onChange={(e) => setFormData({ ...formData, title_deed_number: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Erf/Stand Number</label>
                                    <input type="text" value={formData.erf_number} onChange={(e) => setFormData({ ...formData, erf_number: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                            </div>

                            {/* Financial */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Purchase Price *</label>
                                    <input type="number" step="0.01" min="0" required value={formData.purchase_price}
                                        onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Transfer Duty</label>
                                    <input type="number" step="0.01" min="0" value={formData.transfer_duty}
                                        onChange={(e) => setFormData({ ...formData, transfer_duty: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Currency</label>
                                    <select value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        {currencies.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Client & Trust Account */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Primary Client *</label>
                                    <select required value={formData.primary_client} onChange={(e) => setFormData({ ...formData, primary_client: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        <option value="">Select client...</option>
                                        {clients.map(c => <option key={c.id} value={c.id}>{c.full_name || `${c.first_name} ${c.last_name}`}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Trust Account</label>
                                    <select value={formData.trust_account} onChange={(e) => setFormData({ ...formData, trust_account: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        <option value="">No trust account linked</option>
                                        {trustAccounts.map(a => <option key={a.id} value={a.id}>{a.account_name} ({a.account_number})</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Dates */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instruction Date *</label>
                                    <input type="date" required value={formData.instruction_date}
                                        onChange={(e) => setFormData({ ...formData, instruction_date: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Registration Date</label>
                                    <input type="date" value={formData.target_registration_date}
                                        onChange={(e) => setFormData({ ...formData, target_registration_date: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    rows={2} placeholder="Additional notes..."
                                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShowModal(false)}
                                    className="px-5 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium">
                                    Cancel
                                </button>
                                <button type="submit" disabled={saving}
                                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                                    {saving ? 'Creating...' : 'Create Matter'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConveyancingMatters;
